import { PanelAccess, RoleDefaultAccess } from '../../../shared/services/userService';
import { getCodeDefaultPanelAccess } from './panelAccessDefaults';

/** Valeur booléenne depuis l'API / MySQL (évite Boolean("0") === true). */
export function parseDbAccessFlag(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === '1' || s === 'true') return true;
    if (s === '0' || s === 'false' || s === '') return false;
  }
  return Boolean(v);
}

/** Sections prises en compte pour l'entrée « Panel » (carte, hub). */
export const PANEL_ENTRY_SECTION_IDS: readonly string[] = [
  'admin',
  'moderation',
  'stats',
  'interventions',
  'interventions_manage',
  'player_premium',
  'system',
  'roles',
  'salon_joueurs',
  'salon_security',
  'salon_cm',
  'salon_admin',
  'salon_modo',
  'salon_scenariste',
  'salon_ref',
];

/**
 * Hub « salon_ref » : réservé aux personnes ayant une fonction référent assignée (auth_referent),
 * plus Founder / directions (défaut code). Le rôle Team Advisor (15) n'y a PLUS accès
 * automatiquement — aligné sur Team Member (14) ; le référent est désormais purement une assignation.
 */
export function canAccessSalonRefSection(
  roleId: number,
  referentFunctions: string[],
  userOverrides: PanelAccess[],
  roleDefaults: RoleDefaultAccess[],
): boolean {
  const userRow = userOverrides.find((a) => a.section === 'salon_ref');
  if (userRow !== undefined) {
    return parseDbAccessFlag(userRow.has_access);
  }
  const roleRow = roleDefaults.find((a) => a.section === 'salon_ref');
  if (roleRow !== undefined) {
    return parseDbAccessFlag(roleRow.has_access);
  }
  if (referentFunctions.length > 0) return true;
  return getCodeDefaultPanelAccess('salon_ref', roleId);
}

/**
 * Ordre : **individuel** (auth_panel_access) → **rôle** (auth_role_default_access) → défaut code (+ référents pour salon_ref).
 */
export function computeEffectivePanelAccess(
  sectionId: string,
  roleId: number,
  userOverrides: PanelAccess[],
  roleDefaults: RoleDefaultAccess[],
  referentFunctions: string[] = [],
): boolean {
  if (sectionId === 'salon_ref') {
    return canAccessSalonRefSection(roleId, referentFunctions, userOverrides, roleDefaults);
  }

  const userRow = userOverrides.find((a) => a.section === sectionId);
  if (userRow !== undefined) {
    return parseDbAccessFlag(userRow.has_access);
  }
  const roleRow = roleDefaults.find((a) => a.section === sectionId);
  if (roleRow !== undefined) {
    return parseDbAccessFlag(roleRow.has_access);
  }
  return getCodeDefaultPanelAccess(sectionId, roleId);
}

export function hasAnyEffectivePanelAccess(
  roleId: number,
  userOverrides: PanelAccess[],
  roleDefaults: RoleDefaultAccess[],
  referentFunctions: string[] = [],
): boolean {
  return PANEL_ENTRY_SECTION_IDS.some((id) =>
    computeEffectivePanelAccess(id, roleId, userOverrides, roleDefaults, referentFunctions),
  );
}
